import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import UserPasswordChangeModal from './password-change-modal'
import { updateUser } from '../../super-admin/users/helpers'
import { fetchLoggedUser } from '../../../components/shared/auth'
import { useAppSelector } from '../../../hooks/redux'

interface AuthedUser {
    uid?: string
    email?: string
    name?: string
    surname?: string
    phone?: string
    alarmsNotifiable?: boolean
}

/**
 * User profile page.
 *
 * Lets the authed user edit their own profile fields, opens a modal
 * to change the account password and persists changes via the same
 * helper used by the super-admin user form.
 */
const UserProfile = () => {
    const { t } = useTranslation()
    const authedUser = useAppSelector(state => state.auth.user) as AuthedUser | null

    const [passwordChangeModal, setPasswordChangeModal] = useState(false)
    const [userForm, setUserForm] = useState<AuthedUser>({})

    useEffect(() => {
        if (authedUser) {
            setUserForm(authedUser)
        }
    }, [authedUser])

    const toggleChangePasswordModal = (): void =>
        setPasswordChangeModal(previous => !previous)

    const setUserFormData = (key: keyof AuthedUser, value: unknown): void => {
        setUserForm(previous => ({
            ...previous,
            [key]: value,
        }))
    }

    const handleSave = (): void => {
        if (!authedUser?.uid) {
            return
        }

        updateUser(
            authedUser as unknown as import('../../../types/domain').User,
            userForm as unknown as Partial<import('../../../types/domain').User>,
        )
            .then(() => {
                fetchLoggedUser()
                    .then(() => setUserForm(authedUser))
                window.swalert(t('users.form.saveSuccess'), 'success')
            })
            .catch((error: Error | string) => {
                const message = error instanceof Error ? error.message : String(error)
                window.swalert(message, 'danger')
            })
    }

    return (
        <div className="container col-12 col-md-6">
            <div>
                <div className="form-group">
                    <label htmlFor="user-form-email">
                        {t('users.form.email')} *
                    </label>
                    <input
                        type="email"
                        className="form-control"
                        id="user-form-email"
                        placeholder={t('users.form.email')}
                        value={userForm.email || ''}
                        disabled
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="user-form-name">
                        {t('users.form.name')}
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="user-form-name"
                        placeholder={t('users.form.name')}
                        value={userForm.name || ''}
                        onChange={event => setUserFormData('name', event.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="user-form-surname">
                        {t('users.form.surname')}
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="user-form-surname"
                        placeholder={t('users.form.surname')}
                        value={userForm.surname || ''}
                        onChange={event => setUserFormData('surname', event.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="user-form-phone">
                        {t('users.form.phone')}
                    </label>
                    <input
                        type="tel"
                        className="form-control"
                        id="user-form-phone"
                        placeholder={t('users.form.phone')}
                        value={userForm.phone || ''}
                        onChange={event => setUserFormData('phone', event.target.value)}
                    />
                </div>

                <div className="form-group">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="user-form-alarms-notifiable"
                            onChange={event => setUserFormData('alarmsNotifiable', event.target.checked)}
                            checked={userForm.alarmsNotifiable || false}
                        />
                        <label className="form-check-label" htmlFor="user-form-alarms-notifiable">
                            {t('users.form.alarmsNotifiable')}
                        </label>
                    </div>
                </div>

                <div className="form-group row">
                    <div className="col-sm-10">
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleSave}
                        >
                            {t('users.form.save')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="mt-3 pt-3 border-top border-info">
                <div onClick={toggleChangePasswordModal} className="btn btn-outline-info">
                    {t('auth.passwordChange')}
                </div>
            </div>

            {passwordChangeModal && (
                <UserPasswordChangeModal
                    handleHideModal={toggleChangePasswordModal}
                    backdrop
                    keyboard
                />
            )}
        </div>
    )
}

export default UserProfile
